import {
  AuthorizationCodeAlreadyUsedError,
  AuthorizationCodeExpiredError,
  AuthorizationCodeMissingError,
  BadRequestError,
  ClerkError,
  ConflictError,
  ExpectedError,
  ExternalError,
  ForbiddenError,
  IntegrationInstallationNotFoundError,
  IntegrationNotFoundError,
  InternalError,
  LocationNotFoundError,
  MismatchedOrgIdError,
  NoFacebookPagesFoundError,
  NoOrgIdError,
  NotFoundError,
  NotificationError,
  OAuthError,
  PrismaError,
  UnauthorizedError,
  WebsiteNotFoundError,
} from "../errors";
import { ErrorCode } from "./codes";
import { ERROR_CONFIG, ErrorConfigEntry } from "./config";
import { ERROR_TYPES, ErrorType } from "./errorTypes";
import { getCustomErrorType, isCustomError } from "./utils";

export const ERROR_ORIGIN_FIELD = "rebolt_factory";

export const isFactoryError = (error: unknown): error is ErrorConfigEntry => {
  return typeof error === "object" && error !== null && "factory_code" in error;
};

/**
 * Helper function to get the factory error code from an error.
 * Allows matching based on the factory error code.
 * e.g. (if (getFactoryErrorCode(error) === ERROR_CODES.GOOGLE_TOKEN_REFRESH_FAILED) { ... })
 * @param error - The error to get the factory error code from
 * @returns The factory error code if the error is a factory error, undefined otherwise
 */
export const getFactoryErrorCode = (error: unknown): ErrorCode | undefined => {
  return isFactoryError(error) ? error.factory_code : undefined;
};

/**
 * @description Factory function that creates an already instantiated error, ready to be thrown, of the specific type with cause information
 * @param errorCode - The code of the error defined in the errors/codes.ts file
 * @param error - The error object to be used as the cause of the new error
 * @returns An error instance of the specific type
 */
export function ErrorFactory(
  errorCode: ErrorCode,
  { error, metadata }: { error: unknown; metadata?: Record<string, unknown> | null }
) {
  /* 
    This isFactoryError check is done in case the internal error thrown is a known error in our list
    E.g if we have a function that has many inner tryCatch, we want to be more specific about the error
    If it does not match the format, we stay with the errorCode as the error code
  */
  const errorObject = ERROR_CONFIG[isFactoryError(error) ? error.factory_code : errorCode];
  // Some backwards compatibility for inner errors not thrown by the factory
  if (!isFactoryError(error) && isCustomError(error)) {
    errorObject.type = (getCustomErrorType(error) as ErrorType) ?? errorObject.type;
    errorObject.message = error instanceof Error ? error.message : errorObject.message;
  }
  const customCause = {
    code: errorCode,
    error,
    type: errorObject.type,
    message: errorObject.message,
    metadata: null as Record<string, unknown> | null,
  };

  if (metadata) {
    customCause.metadata = metadata;
  }

  let errorInstance: Error & { origin?: string; factory_code?: ErrorCode; cause?: Record<string, any> | unknown };

  switch (errorObject.type) {
    case ERROR_TYPES.WEBSITE_NOT_FOUND_ERROR:
      // TODO: How to handle this without making it complex?
      errorInstance = new WebsiteNotFoundError("unknown", "unknown", errorObject.message, error);
      break;
    case ERROR_TYPES.INTEGRATION_NOT_FOUND_ERROR:
      // TODO: How to handle this without making it complex?
      errorInstance = new IntegrationNotFoundError("unknown", errorObject.message, error);
      break;
    case ERROR_TYPES.LOCATION_NOT_FOUND_ERROR:
      // TODO: How to handle this without making it complex?
      errorInstance = new LocationNotFoundError("unknown", errorObject.message, error);
      break;
    case ERROR_TYPES.INTEGRATION_INSTALLATION_NOT_FOUND_ERROR:
      errorInstance = new IntegrationInstallationNotFoundError(errorObject.message, error);
      break;
    case ERROR_TYPES.NOT_FOUND_ERROR:
      errorInstance = new NotFoundError(errorObject.message, errorObject.type, error);
      break;
    case ERROR_TYPES.UNAUTHORIZED_ERROR:
      errorInstance = new UnauthorizedError(errorObject.message, errorObject.type, error);
      break;
    case ERROR_TYPES.NO_ORG_ID_ERROR:
      errorInstance = new NoOrgIdError(errorObject.message, error);
      break;
    case ERROR_TYPES.CONFLICT_ERROR:
      errorInstance = new ConflictError(errorObject.message, errorObject.type, error);
      break;
    case ERROR_TYPES.FORBIDDEN_ERROR:
      errorInstance = new ForbiddenError(errorObject.message, errorObject.type, error);
      break;
    case ERROR_TYPES.MISMATCHED_ORG_ID_ERROR:
      errorInstance = new MismatchedOrgIdError(errorObject.message, error);
      break;
    case ERROR_TYPES.OAUTH_ERROR:
      errorInstance = new OAuthError(errorObject.message, errorObject.type, error);
      break;
    case ERROR_TYPES.AUTHORIZATION_CODE_ALREADY_USED_ERROR:
      errorInstance = new AuthorizationCodeAlreadyUsedError(errorObject.message, error);
      break;
    case ERROR_TYPES.AUTHORIZATION_CODE_EXPIRED_ERROR:
      errorInstance = new AuthorizationCodeExpiredError(errorObject.message, error);
      break;
    case ERROR_TYPES.AUTHORIZATION_CODE_MISSING_ERROR:
      errorInstance = new AuthorizationCodeMissingError(errorObject.message, error);
      break;
    case ERROR_TYPES.BAD_REQUEST_ERROR:
      errorInstance = new BadRequestError(errorObject.message, error);
      break;
    case ERROR_TYPES.NO_FACEBOOK_PAGES_FOUND_ERROR:
      errorInstance = new NoFacebookPagesFoundError(error);
      break;
    case ERROR_TYPES.INTERNAL_ERROR:
      errorInstance = new InternalError(errorObject.message, error);
      break;
    case ERROR_TYPES.EXPECTED_ERROR:
      errorInstance = new ExpectedError(errorObject.message, error);
      break;
    case ERROR_TYPES.EXTERNAL_ERROR:
      errorInstance = new ExternalError(errorObject.message, error);
      break;
    case ERROR_TYPES.NOTIFICATION_ERROR:
      errorInstance = new NotificationError(errorObject.message, error);
      break;
    case ERROR_TYPES.PRISMA_ERROR:
      errorInstance = new PrismaError(errorObject.message, 400, undefined, undefined, error);
      break;
    case ERROR_TYPES.CLERK_ERROR:
      errorInstance = new ClerkError(errorObject.message, 401, undefined, error);
      break;
    default:
      // Default to regular Error if no specific match
      errorInstance = new Error(errorObject.message, { cause: error });
  }

  // Overwrite the cause so we have a consistent cause object across all errors handled by the factory
  if (isFactoryError(error) || isCustomError(error)) {
    errorInstance.cause = error.cause;
    ensureMetadataInCause(errorInstance, metadata);
  } else {
    errorInstance.cause = customCause;
  }
  // Created this field to be able to detect if it came from the factory, so we could rethrow it in a bigger try catch
  // Helps for incremental refactoring
  errorInstance.origin = ERROR_ORIGIN_FIELD;
  // Used to keep track of the original error code. Useful for granular error handling
  errorInstance.factory_code = errorCode;

  // This error instance can be thrown as is e.g throw errorInstance
  return errorInstance;
}

function ensureMetadataInCause(
  errorInstance: Error & { cause?: Record<string, any> | unknown },
  metadata?: Record<string, unknown> | null
): void {
  if (
    errorInstance.cause &&
    typeof errorInstance.cause === "object" &&
    (!("metadata" in errorInstance.cause) ||
      ("metadata" in errorInstance.cause && errorInstance.cause.metadata === null)) &&
    metadata
  ) {
    (errorInstance.cause as Record<string, unknown>).metadata = metadata;
  }
}
